import { Injectable } from '@angular/core';
import { File } from '@ionic-native/file';
import { Md5 } from 'ts-md5';
import { BaseService } from '../../core/controllers/base.service';
import { ThumbnailOptions } from './thumbnail-options.model';

interface ThumbnailOptionsFull extends ThumbnailOptions {
    targetPath?: string;
}

@Injectable()
export class CordovaThumbnailService extends BaseService {

    readonly defaultOptions: ThumbnailOptions = {
        maxPixelSize: 120,
        compression: 90
    };

    constructor(
        protected nativeFileService: File
    ) {
        super();
    }

    /**
     * Returns the path to the thumbnail image. If the thumbnail does not
     * already exist, it will be generated.
     * @param url {string} path to the source image
     * @param mimeType {string} mimetype of the image
     * @param options {ThumbnailOptions} options for the thumbnail
     * @todo validate url as cordova local file system path
     */
    getThumbnail(
        url: string,
        mimeType: string,
        options: ThumbnailOptions = {} as any
    ): Promise<string> {

        const thumbnailDir = this.nativeFileService.externalCacheDirectory;

        const opts: ThumbnailOptionsFull = {
            ...this.defaultOptions,
            ...options
        };

        const md5 = Md5.hashStr(`${url}-${opts.maxPixelSize}`);
        const targetPath = `${thumbnailDir}${md5}`;
        opts.targetPath = targetPath;

        return this
            .fileExists(md5.toString(), thumbnailDir)
            .then(exists => {
                return exists ?
                    targetPath :
                    this.createThumbnail(url, mimeType, opts);
            })
            .catch(error => {
                console.warn(error);
                return '';
            });
    }

    async createThumbnail(
        url: string,
        mimeType: string,
        options: ThumbnailOptions
    ): Promise<string> {
        const mimeFamily = mimeType.substring(0, mimeType.indexOf('/'));
        switch (mimeFamily) {
            case 'image': {
                return this.createImageThumbnail(url, options);
            }
            case 'video': {
                return `assets/content-types/video-placeholder.png`;
            }
            default: {
                return `assets/content-types/${mimeFamily}.svg`;
            }
        }
    }

    /**
     * Calls the cordova plugin to generate the thumbnail
     * @param url {string} the local file system path
     * @param options {ThumbnailOptionsFull}
     * @return {Promise<string>} the path to the thumbnail image
     */
    createImageThumbnail(url: string, options: ThumbnailOptionsFull): Promise<string> {
        return new Promise((resolve, reject) => {
            (window as any).Thumbnails.thumbnail(url, options, resolve, reject);
        }).then(() => options.targetPath);
    }

    /**
     * Check if a file exists.
     * @param fileName {string}
     * @param folder {string}
     * @return {Promise<boolean>} true if file exists, false otherwise
     */
    fileExists(fileName: string, folder: string): Promise<boolean> {
        return this.nativeFileService
            .checkFile(folder, fileName)
            .then(() => true)
            .catch(() => false);
    }
}



// WEBPACK FOOTER //
// ./src/app/services/cordova-thumbnail.service.ts